import * as React from "react";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminPagesPageStyles.scss";
import { CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { Chip } from "@progress/kendo-react-buttons";
import { GridFetchResponse, ValueObject } from "shared/types";
import PageDetailsModal, { UpsertPageRecord } from "./PageDetailsModal";
import { useQuery, useQueryClient } from "react-query";
import http from "shared/http";
import { useResourceCategoriesLookupQuery } from "data/lookup/lookupQueries";

interface UseGetPagesForGridQueryOptions {
  title?: string;
  category?: string;
  role?: string;
  tier?: string;
  isActive?: boolean;
  isHidden?: boolean;
  take?: number;
  skip?: number;
  sortBy?: string;
  ascending?: boolean;
}

const useGetPagesForGridQuery = (params: UseGetPagesForGridQueryOptions) => {
  return useQuery(
    ["pages-grid", JSON.stringify(params)],
    async () => {
      const response: GridFetchResponse = await http.fetchAsync(
        "admin/pages/search",
        params,
      );
      return response;
    },
    {
      staleTime: 30 * 1000,
      enabled: true,
    },
  );
};

interface ColumnHandlers {
  onEdit: (page: UpsertPageRecord) => void;
}

const tierMap: Record<string, { color: string; bg: string }> = {
  BRONZE: { color: "#b5651d", bg: "#fdf3e7" },
  SILVER: { color: "#546e7a", bg: "#eceff1" },
  GOLD: { color: "#b8860b", bg: "#fffde7" },
};

const TierBadge: React.FC<{ tier: string }> = ({ tier }) => {
  if (!tier) return null;
  const s = tierMap[tier.toUpperCase()] ?? { color: "#64748b", bg: "#f1f5f9" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: "0.73rem",
        fontWeight: 600,
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.color}40`,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: s.color,
          flexShrink: 0,
        }}
      />
      {tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()}
    </span>
  );
};

const ChipList: React.FC<{ items: string[] }> = ({ items }) => {
  if (!items?.length) return null;
  const visible = items.slice(0, 2);
  const overflow = items.slice(2);
  return (
    <div
      style={{
        display: "flex",
        gap: 3,
        alignItems: "center",
        overflow: "hidden",
        flexWrap: "nowrap",
      }}
    >
      {visible.map((item) => (
        <Chip key={item} text={item} size="small" />
      ))}
      {overflow.length > 0 && (
        <span title={overflow.join(", ")}>
          <Chip text={`+${overflow.length}`} size="small" />
        </span>
      )}
    </div>
  );
};

const createColumns = ({ onEdit }: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [{ label: "Edit", icon: "fa-solid fa-pencil" }],
    onMenuItemClick: (label, dataItem) => {
      if (label === "Edit") onEdit(dataItem as UpsertPageRecord);
    },
  },
  { field: "id", title: "ID", width: 70, hide: true, pinned: true },
  {
    field: "title",
    title: "Title",
    width: 260,
    renderCell: (data: ValueObject) => (
      <CPText noWrap style={{ textTransform: "capitalize" }}>
        {data.dataItem.title}
      </CPText>
    ),
  },
  {
    field: "url",
    title: "URL",
    width: 280,
    renderCell: (data: ValueObject) => (
      <CPText noWrap style={{ fontSize: 13 }}>
        {data.dataItem.url}
      </CPText>
    ),
  },
  {
    field: "iconName",
    title: "Icon",
    width: 160,
    renderCell: (data: ValueObject) => {
      const icon: string = data.dataItem.iconName;
      if (!icon) return null;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i className={icon} style={{ fontSize: 14 }} />
          <CPText noWrap style={{ fontSize: 13 }}>
            {icon}
          </CPText>
        </div>
      );
    },
  },
  {
    field: "mode",
    title: "Mode",
    width: 120,
    renderCell: (data: ValueObject) => {
      const modeLabels: Record<string, string> = {
        NONE: "Same window",
        WINDOW: "New window",
        TAB: "New tab",
      };
      const mode: string = data.dataItem.mode;
      return <CPText>{modeLabels[mode] ?? mode ?? ""}</CPText>;
    },
  },
  {
    field: "isActive",
    title: "Status",
    width: 100,
    renderCell: (data: ValueObject) => (
      <Chip
        text={data.dataItem.isActive ? "Active" : "Inactive"}
        themeColor={data.dataItem.isActive ? "success" : "error"}
        size="small"
      />
    ),
  },
  {
    field: "isHidden",
    title: "Hidden",
    width: 90,
    renderCell: (data: ValueObject) => (
      <Chip
        text={data.dataItem.isHidden ? "Hidden" : "Visible"}
        themeColor={data.dataItem.isHidden ? "warning" : "base"}
        size="small"
      />
    ),
  },
  {
    field: "resourceTier",
    title: "Tier",
    width: 110,
    renderCell: (data: ValueObject) => (
      <TierBadge tier={data.dataItem.resourceTier} />
    ),
  },
  {
    field: "categories",
    title: "Categories",
    width: 220,
    sortable: false,
    renderCell: (data: ValueObject) => (
      <ChipList items={data.dataItem.categories} />
    ),
  },
  {
    field: "roles",
    title: "Roles",
    width: 220,
    sortable: false,
    renderCell: (data: ValueObject) => <ChipList items={data.dataItem.roles} />,
  },
  {
    field: "agencies",
    title: "Agencies",
    width: 220,
    sortable: false,
    renderCell: (data: ValueObject) => (
      <ChipList items={data.dataItem.agencies} />
    ),
  },
];

export const AdminPagesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filterParams, setFilterParams] =
    React.useState<UseGetPagesForGridQueryOptions>({ take: 20, skip: 0 });
  const { data: categories } = useResourceCategoriesLookupQuery();
  const { data: pages, isFetching: isPagesLoading } =
    useGetPagesForGridQuery(filterParams);
  const [selectedPage, setSelectedPage] =
    React.useState<UpsertPageRecord | null>(null);

  const adapter = useCPGridV2({
    columns: createColumns({ onEdit: setSelectedPage }),
    data: pages?.records ?? [],
    showLoader: isPagesLoading,
    totalRecords: pages?.totalRecords ?? 0,
  });

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(() => {
    return [
      {
        filterType: "text",
        label: "Title",
        columnKey: "title",
        placeholder: "e.g. Driver Portal",
      },
      {
        filterType: "dropdown",
        label: "Category",
        columnKey: "categories",
        options:
          categories?.map((category) => ({
            label: category,
            value: category,
          })) ?? [],
      },
      {
        filterType: "dropdown",
        label: "Status",
        columnKey: "isActive",
        options: [
          { label: "Active", value: "true" },
          { label: "Inactive", value: "false" },
        ],
      },
      {
        filterType: "dropdown",
        label: "Visibility",
        columnKey: "isHidden",
        options: [
          { label: "Visible", value: "false" },
          { label: "Hidden", value: "true" },
        ],
      },
      {
        filterType: "dropdown",
        label: "Tier",
        columnKey: "resourceTier",
        options: [
          { label: "Bronze", value: "BRONZE" },
          { label: "Silver", value: "SILVER" },
          { label: "Gold", value: "GOLD" },
        ],
      },
    ];
  }, [categories]);

  return (
    <div className="admin-pages-page">
      <CPGridV2
        name="admin-pages-grid"
        title="Page Management"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetPagesForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        renderActions={() => (
          <CPButton
            fillMode="flat"
            size="small"
            onClick={() => setSelectedPage(new UpsertPageRecord())}
          >
            + New Page
          </CPButton>
        )}
        onRefresh={() => queryClient.invalidateQueries(["pages-grid"])}
      />

      <PageDetailsModal
        key={selectedPage?.id}
        selectedPage={selectedPage}
        onClose={() => setSelectedPage(null)}
        onSave={() => {
          setSelectedPage(null);
          queryClient.invalidateQueries(["pages-grid"]);
        }}
      />
    </div>
  );
};

export default AdminPagesPage;
